import { computed, inject, Injectable, signal } from '@angular/core';
// import { effect } from '@angular/core'; // COUNTER DISATTIVATI
import { environment } from '../environments/environment';
import {
  CampoRicerca,
  ICreateEmail,
  IUpdateEmail,
  IZipExport,
  Mail,
  MailType,
  MessaggioOriginale,
  MittenteSuggerito,
  Ricevuta,
} from '../models/mail.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { IPaginationQueryParams, IPaginationResponse } from '../models/http.model';
import { AllegatoS3, ListaAllegatiS3, UrlDocumento } from '../models/allegati.model';
import { EtichettaTipo } from '../models/etichetta-messaggio.model';
import { WebsocketService } from './websocket.service';
import { forkJoin, map, Observable, of, Subject } from 'rxjs';
import {UtilsService} from './utils.service';

export interface MailCounts {
  all: number;
  byMailbox: Record<string, number>;
}

/** Filtri di GET /messages/count: `tipo` obbligatorio, gli altri opzionali. */
export interface CountParams {
  tipo: MailType;
  /** Restringe a una casella: INBOX per destinatario, OUTBOX/DRAFT per mittente, ARCHIVED entrambi. */
  email?: string;
  /** Solo INBOX: `true` include anche i messaggi etichettati (default backend: esclusi). */
  includeClassified?: boolean;
  /** Aggiunge `letta = false`. */
  soloNonLetti?: boolean;
}

export const TUTTI_I_CONTATORI: readonly MailType[] = ['INBOX', 'OUTBOX', 'DRAFT', 'ARCHIVED'];

@Injectable({
  providedIn: 'root',
})
export class MailService {
  apiVersion = 'v1';
  private apiUrl = `${environment.apiUrl}/api/${this.apiVersion}/messages`;

  readonly websocketService = inject(WebsocketService);
  readonly utilsService = inject(UtilsService);

  // Notifica generica "questo messaggio è cambiato lato server" — usata ad es. dal
  // compose dialog per avvisare la pagina di dettaglio PEC di ricaricare la mail dopo
  // aver registrato un inoltro, dato che sono due componenti che non si parlano altrimenti.
  private readonly mailUpdatedSubject = new Subject<number>();
  readonly mailUpdated$ = this.mailUpdatedSubject.asObservable();

  notifyMailUpdated(mailId: number) {
    this.mailUpdatedSubject.next(mailId);
  }

  // Notifica "è arrivato un nuovo messaggio in Inbox": emessa dalla shell, unico punto in cui
  // vive la subscription WebSocket di re-fetch, e ascoltata dalla pagina Inbox per ricaricare
  // la pagina corrente senza che debba aprire una propria subscription al topic.
  private readonly inboxChangedSubject = new Subject<void>();
  readonly inboxChanged$ = this.inboxChangedSubject.asObservable();

  notifyInboxChanged() {
    this.inboxChangedSubject.next();
  }

  // Notifica "i messaggi archiviati sono cambiati": la pagina Archivio la ascolta e ricarica la
  // pagina corrente con i filtri attivi. Chiunque modifichi l'archivio (archiviazione, cambio di
  // etichetta, eventi WebSocket) può emetterla senza conoscere lo stato della pagina.
  private readonly archivedChangedSubject = new Subject<void>();
  readonly archivedChanged$ = this.archivedChangedSubject.asObservable();

  notifyArchivedChanged() {
    this.archivedChangedSubject.next();
  }

  // Notifica "i messaggi inviati sono cambiati": la pagina Inviati la ascolta e ricarica la
  // pagina corrente con i filtri attivi (casella, solo inoltrati, ordinamento).
  private readonly sentChangedSubject = new Subject<void>();
  readonly sentChanged$ = this.sentChangedSubject.asObservable();

  notifySentChanged() {
    this.sentChangedSubject.next();
  }

  // ── Store dei contatori ──
  // Popolato SOLO dalla shell (unico punto che interroga /count); pagine e sidebar li leggono
  // tramite gli accessor `*CountFor`. Chi ha bisogno di valori aggiornati dopo un'azione chiede
  // un refresh con `requestCountsRefresh`, senza mai chiamare /count direttamente.
  private inboxCounts = signal<MailCounts>({ all: 0, byMailbox: {} });
  private outboxCounts = signal<MailCounts>({ all: 0, byMailbox: {} });
  private draftCounts = signal<MailCounts>({ all: 0, byMailbox: {} });
  private archivedCounts = signal<MailCounts>({ all: 0, byMailbox: {} });

  private readonly countsRefreshSubject = new Subject<readonly MailType[]>();
  /** Richieste di ricalcolo dei contatori: le ascolta solo la shell. */
  readonly countsRefresh$ = this.countsRefreshSubject.asObservable();

  // Implement pagination
  private allMailPaginated = signal<IPaginationResponse | null>(null);
  private allInboxPaginated = signal<IPaginationResponse | null>(null);
  private allSentPaginated = signal<IPaginationResponse | null>(null);
  private allDraftPaginated = signal<IPaginationResponse | null>(null);
  private allArchivedPaginated = signal<IPaginationResponse | null>(null);

  // computed
  mailPaginated = computed(() => this.allMailPaginated());
  inboxPaginated = computed(() => this.allInboxPaginated());
  outboxPaginated = computed(() => this.allSentPaginated());
  draftPaginated = computed(() => this.allDraftPaginated());
  archivedPaginated = computed(() => this.allArchivedPaginated());

  constructor(private http: HttpClient) {
    // COUNTER DISATTIVATI: numero di non letti nel titolo della tab
    // effect(() => {
    //   const n = this.inboxCounts().all;
    //   document.title = n > 0 ? `(${n}) Messaggi non letti - PEC Engine` : 'PEC Engine';
    // });
  }

  getInbox(
    pagination: IPaginationQueryParams | null = null,
    email?: string,
    locked: boolean = false,
  ) {
    let url = this.apiUrl + '?tipo=INBOX';
    if (email) url += `&email=${encodeURIComponent(email)}`;
    if (pagination) {
      url += '&pagination=true';
      url += `&pageNumber=${pagination.pageIndex}&pageSize=${pagination.pageSize}`;
      if (pagination.sortDirection) url += `&sortDirection=${pagination.sortDirection}`;
    }
    return this.http.get<IPaginationResponse>(url);
  }

  getMessageById(idRecord: any) {
    let url = `${this.apiUrl}?id=${encodeURIComponent(idRecord)}`;
    // TODO: Set lock with idRiferimento
    return this.http.get<Mail>(url);
  }

  getMessageByIdRiferimento(idRiferimento: string) {
    let url = `${this.apiUrl}/${encodeURIComponent(idRiferimento)}`;
    const lockState = localStorage.getItem('message-lock');
    if (lockState != null) {
      const state = JSON.parse(lockState);
      if (state.idRiferimento == idRiferimento && state.uuid) {
        url += '?uuidLock=' + state.uuid;
      }
    }
    return this.http.get<Mail>(url);
  }

  getOutbox(pagination: IPaginationQueryParams | null = null, email?: string, soloInoltrati = false) {
    let url = this.apiUrl + '?tipo=OUTBOX';
    if (email) url += `&email=${encodeURIComponent(email)}`;
    if (soloInoltrati) url += '&soloInoltrati=true';
    if (pagination) {
      url += '&pagination=true';
      url += `&pageNumber=${pagination.pageIndex}&pageSize=${pagination.pageSize}`;
      if (pagination.sortDirection) url += `&sortDirection=${pagination.sortDirection}`;
    }
    return this.http.get<IPaginationResponse>(url);
  }

  getDraft(pagination: IPaginationQueryParams | null = null, email?: string) {
    let url = this.apiUrl + '?tipo=DRAFT';
    if (email) url += `&email=${encodeURIComponent(email)}`;
    if (pagination) {
      url += '&pagination=true';
      url += `&pageNumber=${pagination.pageIndex}&pageSize=${pagination.pageSize}`;
      if (pagination.sortDirection) url += `&sortDirection=${pagination.sortDirection}`;
    }
    return this.http.get<IPaginationResponse>(url);
  }

  getArchived(pagination: IPaginationQueryParams | null = null, email?: string) {
    let url = this.apiUrl + '?tipo=ARCHIVED';
    if (email) url += `&email=${encodeURIComponent(email)}`;
    if (pagination) {
      url += '&pagination=true';
      url += `&pageNumber=${pagination.pageIndex}&pageSize=${pagination.pageSize}`;
      if (pagination.sortDirection) url += `&sortDirection=${pagination.sortDirection}`;
    }
    return this.http.get<IPaginationResponse>(url);
  }

  /**
   * Messaggi che portano una data etichetta, paginati. Il filtro è applicato dal database:
   * `totalElements` conta già i soli messaggi etichettati. `tipo` restringe alla sezione
   * (INBOX/ARCHIVED); omesso, restituisce tutti i tipi.
   */
  getMessaggiDaEtichetta(
    idEtichetta: number,
    tipo?: EtichettaTipo,
    pagination: IPaginationQueryParams | null = null,
  ) {
    let url = `${this.apiUrl}/etichetta?idEtichetta=${idEtichetta}`;
    if (tipo) url += `&tipo=${tipo}`;
    if (pagination) {
      url += `&pageNumber=${pagination.pageIndex}&pageSize=${pagination.pageSize}`;
      if (pagination.sortDirection) url += `&sortDirection=${pagination.sortDirection}`;
    }
    return this.http.get<IPaginationResponse>(url);
  }

  /**
   * Ricerca trasversale su tutte le sezioni (INBOX/OUTBOX/DRAFT/ARCHIVED): ogni risultato porta
   * il proprio `tipo`, così il pannello di ricerca sa da quale sezione mostrare il chip.
   */
  searchMessages(
    query: string,
    campi: CampoRicerca[],
    pagination: IPaginationQueryParams,
    email?: string,
  ) {
    let url = `${this.apiUrl}/ricerca?query=${encodeURIComponent(query)}&campi=${campi.join(',')}`;
    url += `&pageNumber=${pagination.pageIndex}&pageSize=${pagination.pageSize}`;
    if (pagination.sortDirection) url += `&sortDirection=${pagination.sortDirection}`;
    if (email) url += `&email=${encodeURIComponent(email)}`;
    return this.http.get<IPaginationResponse>(url);
  }

  /** Mittenti (indirizzo + nome) che matchano il testo digitato, per la prima sezione del pannello di ricerca. */
  suggestSenders(query: string) {
    const url = `${this.apiUrl}/mittenti-suggeriti?query=${encodeURIComponent(query)}`;
    return this.http.get<MittenteSuggerito[]>(url);
  }

  /** Destinatari (indirizzo + nome) che matchano il testo digitato, per la prima sezione del pannello di ricerca. */
  suggestRecipients(query: string) {
    const url = `${this.apiUrl}/destinatari-suggeriti?query=${encodeURIComponent(query)}`;
    return this.http.get<MittenteSuggerito[]>(url);
  }

  getAllegatiS3(idRiferimento: string) {
    const url = `${this.apiUrl}/allegati?idRiferimentoPec=${encodeURIComponent(idRiferimento)}`;
    return this.http.get<AllegatoS3[]>(url);
  }

  getZipExport(id: number) {
    const url = `${this.apiUrl}/zip?id=${encodeURIComponent(id)}`;
    return this.http.post<IZipExport>(url, {});
  }

  sendEmail(email: ICreateEmail) {
    return this.http.post<Mail>(this.apiUrl + '/send', email);
  }

  archiviaMessaggio(id: number, idEtichetta: number) {
    return this.http.put<Mail>(`${this.apiUrl}/archivia/${id}`, { idEtichetta });
  }

  /**
   * L'endpoint di archiviazione lavora su un messaggio alla volta: per le azioni massive
   * inoltriamo una richiesta per messaggio e attendiamo il completamento di tutte.
   * Archiviare classifica anche: l'etichetta è obbligatoria.
   */
  archiviaMessaggi(ids: number[], idEtichetta: number): Observable<Mail[]> {
    if (ids.length === 0) return of([]);
    return forkJoin(ids.map(id => this.archiviaMessaggio(id, idEtichetta)));
  }

  segnaComeLetti(ids: number[], letta: boolean) {
    return this.http.put<void>(`${this.apiUrl}/letta`, { ids, letta });
  }

  saveDraft(email: ICreateEmail) {
    return this.http.post<Mail>(this.apiUrl + '/draft', email);
  }

  sendDraftAsEmail(id: number, dto: IUpdateEmail) {
    return this.http.put<Mail>(`${this.apiUrl}/${id}`, dto);
  }

  inoltraMessaggio(idRecord: number, destinatari: string[], corpoMessaggio: string,
                 mittente: string, allegatiOriginali: string[], allegati: AllegatoS3[]) {
  return this.http.put<void>(`${this.apiUrl}/inoltro`,
    { idRecord, destinatari, corpoMessaggio, mittente, allegatiOriginali, allegati });
}

  getListaAllegati(idMessaggio: number) {
    const url = `${this.apiUrl}/lista-allegati?idMessaggio=${encodeURIComponent(idMessaggio)}`;
    return this.http.get<ListaAllegatiS3>(url);
  }

  /** URL presigned del PDF convertito: il file arriva direttamente da S3, non dal service-manager. */
  getPdfConvertitoUrl(idMessaggio: number) {
    const url = `${this.apiUrl}/convertito/url?idMessaggio=${encodeURIComponent(idMessaggio)}`;
    return this.http.get<UrlDocumento>(url);
  }

  /** URL presigned di un allegato; con `download` S3 lo serve come attachment (salvataggio su disco). */
  getAllegatoUrl(idMessaggio: number, nomeFile: string, download = false) {
    const params = new HttpParams()
      .set('idMessaggio', idMessaggio)
      .set('nomeFile', nomeFile)
      .set('download', download);
    return this.http.get<UrlDocumento>(`${this.apiUrl}/allegati/url`, { params });
  }

  /** `useLoader = false`: niente loader globale, per i ricaricamenti in background. */
  fetchInbox(pagination: IPaginationQueryParams, email?: string, useLoader = true) {
    if (useLoader) this.utilsService.startLoading();
    this.getInbox(pagination, email).subscribe({
      next: data => {
        this.allInboxPaginated.set(data);
        if (useLoader) this.utilsService.stopLoading();
      },
      error: err => {
        console.log(err)
        if (useLoader) this.utilsService.stopLoading();
      },
    });
  }

  /** `useLoader = false`: niente loader globale, per i ricaricamenti in background. */
  fetchOutbox(
    pagination: IPaginationQueryParams,
    email?: string,
    soloInoltrati = false,
    useLoader = true,
  ) {
    if (useLoader) this.utilsService.startLoading();
    this.getOutbox(pagination, email, soloInoltrati).subscribe({
      next: data => {
        this.allSentPaginated.set(data);
        if (useLoader) this.utilsService.stopLoading();
      },
      error: err => {
        console.log(err)
        if (useLoader) this.utilsService.stopLoading();
      },
    });
  }

  fetchDraft(pagination: IPaginationQueryParams, email?: string) {
    this.utilsService.startLoading();
    this.getDraft(pagination, email).subscribe({
      next: data => {
        this.allDraftPaginated.set(data);
        this.utilsService.stopLoading();

      },
      error: err => {
        console.log(err);
        this.utilsService.stopLoading();
      },
    });
  }

  /** `useLoader = false`: niente loader globale, per i ricaricamenti in background. */
  fetchArchived(pagination: IPaginationQueryParams, email?: string, useLoader = true) {
    if (useLoader) this.utilsService.startLoading();
    this.getArchived(pagination, email).subscribe({
      next: data => {
        this.allArchivedPaginated.set(data);
        if (useLoader) this.utilsService.stopLoading();
      },
      error: err => {
        console.log(err)
        if (useLoader) this.utilsService.stopLoading();
      },
    });
  }

  /**
   * Popola la lista paginata della sezione indicata con i soli messaggi che portano
   * l'etichetta scelta: alimenta gli stessi signal dei fetch per sezione, così le pagine
   * continuano a leggere `inboxPaginated()` / `archivedPaginated()` senza saperne nulla.
   */
  fetchByEtichetta(
    pagination: IPaginationQueryParams,
    idEtichetta: number,
    tipo: EtichettaTipo,
  ) {
    this.getMessaggiDaEtichetta(idEtichetta, tipo, pagination).subscribe({
      next: data => {
        if (tipo === 'ARCHIVED') {
          this.allArchivedPaginated.set(data);
        } else {
          this.allInboxPaginated.set(data);
        }
      },
      error: err => console.log(err),
    });
  }

  /** GET /messages/count. Da usare solo dalla shell: le viste leggono lo store. */
  getCount(filtri: CountParams): Observable<number> {
    let params = new HttpParams().set('tipo', filtri.tipo);
    if (filtri.email) params = params.set('email', filtri.email);
    if (filtri.includeClassified !== undefined) {
      params = params.set('includeClassified', String(filtri.includeClassified));
    }
    if (filtri.soloNonLetti !== undefined) {
      params = params.set('soloNonLetti', String(filtri.soloNonLetti));
    }
    return this.http
      .get<{ count: number }>(`${this.apiUrl}/count`, { params })
      .pipe(map(res => res?.count ?? 0));
  }

  /**
   * Chiede alla shell di ricalcolare i contatori indicati (default: tutti). Le richieste
   * ravvicinate vengono accorpate dalla shell in un unico giro di chiamate.
   */
  requestCountsRefresh(...tipi: MailType[]) {
    this.countsRefreshSubject.next(tipi.length ? tipi : TUTTI_I_CONTATORI);
  }

  /** Scrive nello store i contatori di una sezione: lo usa solo la shell. */
  setCounts(tipo: MailType, counts: MailCounts) {
    switch (tipo) {
      case 'INBOX':
        this.inboxCounts.set(counts);
        break;
      case 'OUTBOX':
        this.outboxCounts.set(counts);
        break;
      case 'DRAFT':
        this.draftCounts.set(counts);
        break;
      case 'ARCHIVED':
        this.archivedCounts.set(counts);
        break;
    }
  }

  /** Da classificare: INBOX non letti, esclusi i classificati (sidebar e tab vista aggregata). */
  inboxCountFor(email?: string): number {
    const counts = this.inboxCounts();
    return email ? (counts.byMailbox[email] ?? 0) : counts.all;
  }

  decrementInboxCount(email?: string) {
    this.inboxCounts.update(counts => ({
      all: Math.max(0, counts.all - 1),
      byMailbox: email
        ? { ...counts.byMailbox, [email]: Math.max(0, (counts.byMailbox[email] ?? 0) - 1) }
        : counts.byMailbox,
    }));
  }

  outboxCountFor(email?: string): number {
    const counts = this.outboxCounts();
    return email ? (counts.byMailbox[email] ?? 0) : counts.all;
  }

  draftCountFor(email?: string): number {
    const counts = this.draftCounts();
    return email ? (counts.byMailbox[email] ?? 0) : counts.all;
  }

  archivedCountFor(email?: string): number {
    const counts = this.archivedCounts();
    return email ? (counts.byMailbox[email] ?? 0) : counts.all;
  }

  /**
   * Ricevute di accettazione e di avvenuta consegna di quel messaggio, ordinate per data:
   * GET /messages/{id}/ricevute con l'id numerico del messaggio (non l'idRiferimento).
   *
   * Sono sempre e solo le ricevute della spedizione richiesta: per una PEC nata da un inoltro
   * non arrivano quelle del messaggio originale. Il campo `origine` dice quale dei due casi e'.
   */
  getRicevute(idMessaggio: number): Observable<Ricevuta[]> {
    return this.http.get<Ricevuta[]>(`${this.apiUrl}/${idMessaggio}/ricevute`);
  }

  /**
   * La PEC che questa ricevuta attesta: GET /messages/{id}/originale, il percorso inverso di
   * `getRicevute`.
   *
   * Emette `null` quando non c'è un originale da aprire — il messaggio non è una ricevuta,
   * oppure attesta una PEC spedita fuori da questo sistema, che a database non c'è. In entrambi
   * i casi il backend risponde 204, e HttpClient consegna un corpo vuoto come `null`: non è un
   * errore e non va gestito come tale.
   */
  getMessaggioOriginale(idMessaggio: number): Observable<MessaggioOriginale | null> {
    return this.http.get<MessaggioOriginale | null>(`${this.apiUrl}/${idMessaggio}/originale`);
  }
}
